import { Tabs } from '@components/utility/tabs';
import { APP_PREFIX_PATH } from '@constants/common';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, Button, IconButton, Slide, Stack, Typography } from '@mui/material';
import { ReadNotificationsBox } from '@notifications/components/display/boxes/ReadNotificationsBox';
import { UnreadNotificationsBox } from '@notifications/components/display/boxes/UnreadNotificationsBox';
import { useNotificationsBoxContext } from '@notifications/contexts';
import { NotificationTabValues } from '@notifications/types';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

export const NotificationsBox = () => {
    const [tab, setTab] = useState<NotificationTabValues>(NotificationTabValues.UNREAD);

    const {
        unreadNotificationsTotalCount,
        isReadActionRunning,
        tabsActionRef,
        handleMarkAllAsRead,
        isMarkAllReadLoading,
    } = useNotificationsBoxContext();

    const slideContainerRef = useRef<HTMLDivElement>(null);

    return (
        <Box
            px={{ xs: 0, md: '20px' }}
            py={{ xs: 0, md: '10px' }}
            width={{ xs: '100%', md: '500px' }}
            height="100%"
            display="flex"
            flexDirection="column"
            minHeight={0}
        >
            <Tabs
                value={tab}
                onChange={(_, newValue) => setTab(newValue)}
                classes={{ list: 'gap-[24px]' }}
                action={tabsActionRef}
                sx={{ flexShrink: 0 }}
                endElement={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {tab === NotificationTabValues.UNREAD && (
                            <Button
                                variant="text"
                                disabled={unreadNotificationsTotalCount === 0 || isReadActionRunning}
                                onClick={handleMarkAllAsRead}
                                loading={isMarkAllReadLoading || isReadActionRunning}
                                loadingPosition="end"
                            >
                                <Typography fontSize={12} fontWeight={500}>
                                    <FormattedMessage
                                        id="notifications.markAllAsRead"
                                        defaultMessage="Mark all as read"
                                    />
                                </Typography>
                            </Button>
                        )}
                        <IconButton
                            sx={{
                                width: 24,
                                height: 24,
                                p: 0,
                                color: 'black.main',
                                '&:hover': {
                                    color: 'primary-700.main',
                                },
                            }}
                            disableRipple
                            component={Link}
                            to={APP_PREFIX_PATH + '/accounts/details/settings'}
                        >
                            <SettingsOutlinedIcon sx={{ fontSize: 20, color: 'inherit' }} />
                        </IconButton>
                    </Stack>
                }
            >
                <Tabs.Tab
                    className="px-0 min-w-0"
                    value={NotificationTabValues.UNREAD}
                    label={<FormattedMessage id="notifications.unread" defaultMessage="Unread" />}
                />
                <Tabs.Tab
                    className="px-0 min-w-0"
                    value={NotificationTabValues.READ}
                    label={<FormattedMessage id="notifications.read" defaultMessage="Read" />}
                />
            </Tabs>
            <Box
                overflow="hidden"
                width="100%"
                position="relative"
                flex={{
                    xs: 1,
                    md: 'unset',
                }}
                height={{
                    md: '400px',
                }}
                ref={slideContainerRef}
            >
                <Slide
                    in={tab === NotificationTabValues.UNREAD}
                    direction="right"
                    container={slideContainerRef.current}
                >
                    <Box position="absolute" sx={{ inset: 0 }}>
                        <UnreadNotificationsBox />
                    </Box>
                </Slide>
                <Slide in={tab === NotificationTabValues.READ} direction="left" container={slideContainerRef.current}>
                    <Box position="absolute" sx={{ inset: 0 }}>
                        <ReadNotificationsBox />
                    </Box>
                </Slide>
            </Box>
        </Box>
    );
};
